<script lang="ts">
    import { onMount } from 'svelte';
    import ActionHero from '../../lib/Actions/ActionHero.svelte';
    import ActionForm from '../../lib/Actions/ActionForm.svelte';
    import RememberMeBanner from '../../lib/Actions/RememberMeBanner.svelte';
    import EmailVerification from '../../lib/EmailVerification.svelte';
    import Modal from '../../lib/Modal.svelte';
    import { setConfirmationData } from '../../stores/confirmationStore';
    import PetitionText from '../../lib/Actions/PetitionText.svelte';
    import SignPageSkeleton from '../../lib/Petitions/SignPageSkeleton.svelte';
    import Button from '../../lib/Button.svelte';
    import * as t from '../../translations/Petitions.ts';
    import { getPetitionById, getPetitions, signPetition, getUserPetitionSignatureStatus, getPetitionShareUrls, getSignedPetitionIdsForUser, type Petition, type SignPetitionRequest } from '../../api/petitions';
    import { 
        getPetitionFormSettings, 
        type FormFieldSettings, 
        type CustomField 
    } from '../../api/formSettings';
    import { vvSettings, ApiResponseError, getQueryParam, associationIdName } from '../../api/common';
    import { 
        postUser, 
        putUser, 
        postMemberships, 
        postRememberMeCookies,
        deleteRememberMeCookies,
        getUser,
        getMemberships,
        type User,
        type CustomFieldValuePayload,
        type UserIdentity
    } from '../../api/users';
    import { checkAddress, type Address } from '../../api/address';
    import { tick } from 'svelte';
    import { initCkEditorEmbeds } from '../../lib/initCkEditorEmbeds';

    // Get base URL for sharing - use dev URL on localhost for testing
    function getShareBaseUrl(): string {
        const origin = window.location.origin;
        if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
            return 'https://dev.votervoice.net';
        }
        return origin;
    }

    function getGenericShareUrl(petitionUrl: string): string {
        return `${getShareBaseUrl()}${petitionUrl}`;
    }

    function getShareTokenStorageKey(petitionId: number): string {
        return `vv-petition-share-user-token-${petitionId}`;
    }

    async function getShareData(petitionId: number, petitionUrl: string, userToken?: string | null) {
        const genericShareUrl = getGenericShareUrl(petitionUrl);
        const shareUrls = await getPetitionShareUrls(petitionId, userToken || undefined, getQueryParam('vvsn') || undefined);

        return {
            shareUrl: shareUrls?.webShareUrl || genericShareUrl,
            facebookShareUrl: shareUrls?.facebookShareUrl,
            twitterShareUrl: shareUrls?.twitterShareUrl,
            linkedInShareUrl: shareUrls?.linkedInShareUrl
        };
    }

    function getAnswerType(answerType: string | undefined): string {
        return (answerType || '').toLowerCase();
    }

    function isMultiChoiceAnswerType(answerType: string | undefined): boolean {
        const normalized = getAnswerType(answerType);
        return normalized.includes('multipleanswers')
            || normalized.includes('multianswer')
            || normalized.includes('multi_choice')
            || normalized.includes('multiplechoice')
            || normalized.includes('checkboxlist');
    }

    function buildCustomFieldPayload(): CustomFieldValuePayload[] {
        return customFields.flatMap((field) => {
            const rawValue = customFieldValues[field.id];
            if (rawValue === undefined || rawValue === null) return [];

            if (typeof rawValue === 'string' && rawValue.trim() === '') return [];

            const values = isMultiChoiceAnswerType(field.answerType)
                ? String(rawValue).split('|').filter(v => v)
                : [rawValue];

            return values.length > 0 ? [{ id: field.id, values }] : [];
        });
    }

    export let params: { id?: string } = {};

    /** Set when index auto-redirects because only one petition exists (no list to go back to). */
    $: hideBackToPetitions = getQueryParam('fromSingle') === '1';

    function getSiteSegment(): string {
        const rawSiteNumber = (vvSettings as any)?.siteNumber;
        return rawSiteNumber ? `/${rawSiteNumber}` : '';
    }

    function navigateToConfirmation(petitionId: number): void {
        const q = getQueryParam('fromSingle') === '1' ? '?fromSingle=1' : '';
        window.location.href = `/${associationIdName}${getSiteSegment()}/Petitions/${petitionId}/ConfirmResponse${q}`;
    }

    // Page states (success navigates to /Confirmation/:id)
    type PageState = 'loading' | 'form' | 'submitting' | 'error';
    let pageState: PageState = 'loading';
    let errorMessage = '';
    let isAwaitingVerification = false;
    let verificationEmailAddress = '';
    let verifiedIdentity: UserIdentity | null = null;
    let identityChoices: UserIdentity[] = [];
    let selectedIdentityToken = '';
    let isChoosingIdentity = false;

    // Petition data structure for the page
    interface PetitionData {
        id: number;
        title: string;
        description: string;
        petitionText: string;
        imageUrl: string | null;
        dueDate: string | null;
        signatureCount: number;
        signatureGoal: number | null;
        spotlighted: boolean;
        url: string;
        confirmationMessage?: string | null;
        sharingEnabled?: boolean;
        hideAlert?: boolean | false;
    }

    let petition: PetitionData | null = null;
    let currentLoadedId: string | null = null;
    let isEmbeddedPluginContext = false;
    const alertCollapsedHeight = 180;
    let descriptionExpanded = false;
    let descriptionNeedsExpansion = false;
    let descriptionElement: HTMLElement;
    const REMEMBERED_REFRESH_TIMEOUT_MS = 1200;

    $: if (descriptionElement) {
        descriptionNeedsExpansion = descriptionElement.scrollHeight > alertCollapsedHeight;
    }

    $: hasSignatureGoal = petition?.signatureGoal !== null && (petition?.signatureGoal ?? 0) > 0;
    $: signatureGoalValue = hasSignatureGoal ? Number(petition?.signatureGoal) : 0;
    $: signatureProgressPercent = hasSignatureGoal
        ? Math.min(((petition?.signatureCount ?? 0) / signatureGoalValue) * 100, 100)
        : 0;
    $: signaturesRemaining = hasSignatureGoal
        ? Math.max(signatureGoalValue - (petition?.signatureCount ?? 0), 0)
        : 0;

    function getRemainingSignaturesText(count: number): string {
        return `${count.toLocaleString()} more ${count === 1 ? 'signature' : 'signatures'} needed`;
    }

    function getExceededSignaturesText(count: number): string {
        return `Goal exceeded by ${count.toLocaleString()} ${count === 1 ? 'signature' : 'signatures'}`;
    }

    // Form settings from Action Settings Center (loaded from vvSettings)
    // These are injected server-side, similar to electionCenter
    const petitionFormConfig = getPetitionFormSettings();
    let formSettings: FormFieldSettings = petitionFormConfig.fieldSettings;
    let customFields: CustomField[] = petitionFormConfig.customFields;

    // Load petition data
    async function loadPetition(id: string) {
        // Skip if already loaded this petition
        if (currentLoadedId === id && petition) return;
        
        pageState = 'loading';
        errorMessage = '';
        currentLoadedId = id;
        
        try {
            const petitionId = parseInt(id, 10);
            if (isNaN(petitionId)) {
                throw new Error(t.Invalid_Petition_Id());
            }

            const rememberedRefreshPromise = refreshRememberedState()
                .catch(() => null)
                .finally(() => {
                    showRememberBanner = rememberedUser !== null;
                });
            await Promise.race([
                rememberedRefreshPromise,
                new Promise<void>((resolve) => setTimeout(resolve, REMEMBERED_REFRESH_TIMEOUT_MS))
            ]);

            const apiPetition = await getPetitionById(petitionId);
            
            if (!apiPetition) {
                throw new Error(t.Petition_Not_Found());
            }

            // Map API response to page data structure
            petition = {
                id: apiPetition.id,
                title: apiPetition.title,
                description: apiPetition.description || '',
                petitionText: apiPetition.officialText,
                imageUrl: apiPetition.imageUrl || null,
                dueDate: apiPetition.endDate || null,
                signatureCount: apiPetition.signatureCount || 0,
                signatureGoal: apiPetition.signatureGoal ?? null,
                spotlighted: apiPetition.spotlighted,
                url: apiPetition.url,
                confirmationMessage: apiPetition.confirmationMessage,
                sharingEnabled: apiPetition.sharingEnabled,
                hideAlert: apiPetition.hideAlert,
            };
            descriptionExpanded = false;
            
            // Check if user has already signed this petition (like old page does)
            const userToken = userContainer?.userModel?.userToken;
            if (userToken) {
                const signatureStatus = await getUserPetitionSignatureStatus(petitionId, userToken);
                if (signatureStatus?.hasSigned) {
                    // User already signed - redirect to confirmation with date
                    await loadRelatedPetitions(petitionId);
                    sessionStorage.setItem(getShareTokenStorageKey(petition.id), userToken);
                    
                    const shareData = await getShareData(petition.id, petition.url, userToken);

                    setConfirmationData({
                        petitionId: petition.id,
                        petitionTitle: petition.title,
                        signatureCount: petition.signatureCount && petition.signatureCount > 0 ? petition.signatureCount : undefined,
                        signatureGoal: petition.signatureGoal,
                        shareUrl: shareData.shareUrl,
                        facebookShareUrl: shareData.facebookShareUrl,
                        twitterShareUrl: shareData.twitterShareUrl,
                        linkedInShareUrl: shareData.linkedInShareUrl,
                        thankYouMessage: petition.confirmationMessage || 'Thank you for signing this petition!',
                        sharingEnabled: petition.sharingEnabled ?? true,
                        relatedPetitions,
                        datePreviouslySigned: signatureStatus.dateSigned || null,
                        hideAlert: apiPetition.hideAlert,
                    });
                    navigateToConfirmation(petition.id);
                    return;
                }
            }
            
            pageState = 'form';
            await tick();
            scrollToTopInstant();

            if (!petition) {
                errorMessage = t.Failed_To_Load_Petition();
                pageState = 'error';
            }
        } catch (error) {
            console.error('Failed to load petition:', error);
            errorMessage = error instanceof Error ? error.message : t.Failed_To_Load_Petition();
            pageState = 'error';
            await tick();
            scrollToTopInstant();
        }
    }

    function scrollToTopInstant() {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }

    function detectEmbeddedPluginContext(): boolean {
        const inIframe = (() => {
            try {
                return window.self !== window.top;
            } catch {
                return true;
            }
        })();
        const iframePath = window.location.pathname.toLowerCase().startsWith('/iframes');
        return inIframe || iframePath;
    }

    // Load on mount
    onMount(() => {
        isEmbeddedPluginContext = detectEmbeddedPluginContext();
        scrollToTopInstant();
        if (params.id) {
            loadPetition(params.id);
        } else {
            errorMessage = t.No_Petition_Id_Provided();
            pageState = 'error';
        }
    });

    // React to param changes (for related petition navigation)
    $: if (params.id && params.id !== currentLoadedId) {
        // Reset state for new petition
        scrollToTopInstant();
        pageState = 'loading';
        loadPetition(params.id);
    }

    // Related petitions for confirmation page (loaded from API)
    let relatedPetitions: Array<{
        id: number;
        title: string;
        imageUrl: string | null;
        signatureCount: number;
        hasSigned?: boolean;
    }> = [];

    // Load related petitions (other petitions excluding current one)
    async function loadRelatedPetitions(currentId: number) {
        try {
            const response = await getPetitions();
            const allPetitions = [...response.spotlightPetitions, ...response.otherPetitions];
            
            // Filter out current petition and take up to 3 related ones
            let rows = allPetitions
                .filter(p => p.id !== currentId)
                .slice(0, 3)
                .map(p => ({
                    id: p.id,
                    title: p.title,
                    imageUrl: p.imageUrl || null,
                    signatureCount: p.signatureCount || 0
                }));

            const userToken = userContainer?.userModel?.userToken?.trim();
            if (userToken && rows.length > 0) {
                const signedRelated = await getSignedPetitionIdsForUser(
                    userToken,
                    rows.map((r) => r.id)
                );
                rows = rows.map((r) => ({ ...r, hasSigned: signedRelated.has(r.id) }));
            }
            relatedPetitions = rows;
        } catch (error) {
            console.error('Failed to load related petitions:', error);
            relatedPetitions = [];
        }
    }

    // Get remembered user data from server-injected vvSettings
    // userContainer is populated by the Razor view from session/cookie data
    const rootSettings = vvSettings as any;
    const userContainer = rootSettings.userContainer || (rootSettings.userContainer = {});
    let isRemembered = rootSettings.isRemembered ?? false;
    const rememberMeDefault = rootSettings?.rememberMeDefault ?? true;
    
    function pickFirstNonEmpty(...values: Array<string | undefined | null>): string {
        for (const value of values) {
            if (typeof value === 'string' && value.trim().length > 0) return value;
        }
        return '';
    }

    function normalizeAddress(address: any) {
        if (!address) return undefined;
        return {
            street: pickFirstNonEmpty(address.street, address.streetAddress),
            city: pickFirstNonEmpty(address.city),
            state: pickFirstNonEmpty(address.state),
            zipCode: pickFirstNonEmpty(address.postalCode, address.zipCode)
        };
    }

    function getValidatedAddress(address: any): Address | null {
        if (!address) return null;

        const streetAddress = pickFirstNonEmpty(address.streetAddress, address.street);
        const city = pickFirstNonEmpty(address.city);
        const state = pickFirstNonEmpty(address.state);
        const zipCode = pickFirstNonEmpty(address.zipCode, address.postalCode);
        const checksum = pickFirstNonEmpty(address.checksum);

        if (!streetAddress || !zipCode || !checksum) {
            return null;
        }

        return {
            streetAddress,
            city,
            state,
            zipCode,
            zipCodeExtension: pickFirstNonEmpty(address.zipCodeExtension),
            county: pickFirstNonEmpty(address.county),
            country: pickFirstNonEmpty(address.country),
            checksum
        };
    }

    function normalizeAddressForComparison(address: any) {
        if (!address) return null;

        const streetAddress = pickFirstNonEmpty(address.streetAddress, address.street);
        const city = pickFirstNonEmpty(address.city);
        const state = pickFirstNonEmpty(address.state);
        const zipCode = pickFirstNonEmpty(address.zipCode, address.postalCode);

        if (!streetAddress && !city && !state && !zipCode) {
            return null;
        }

        return {
            streetAddress,
            city,
            state,
            zipCode
        };
    }

    function addressesMatch(current: { streetAddress: string; city: string; state: string; zipCode: string }, original: any): boolean {
        const normalizedOriginal = normalizeAddressForComparison(original);
        const normalizedCurrent = normalizeAddressForComparison(current);

        if (!normalizedOriginal && !normalizedCurrent) return true;
        if (!normalizedOriginal || !normalizedCurrent) return false;

        return normalizedOriginal.streetAddress === normalizedCurrent.streetAddress
            && normalizedOriginal.city === normalizedCurrent.city
            && normalizedOriginal.state === normalizedCurrent.state
            && normalizedOriginal.zipCode === normalizedCurrent.zipCode;
    }

    function hasRememberedProfileChanges(shouldIncludeHomeAddress: boolean, shouldIncludeBusinessAddress: boolean): boolean {
        const rememberedModel = userContainer?.userModel;
        if (!rememberedUser || !rememberedModel?.userToken) {
            return true;
        }

        const personalInfoChanged =
            honorific !== pickFirstNonEmpty(rememberedModel.honorific) ||
            firstName !== pickFirstNonEmpty(rememberedModel.firstName, rememberedModel.givenNames) ||
            lastName !== pickFirstNonEmpty(rememberedModel.lastName, rememberedModel.surname) ||
            suffix !== pickFirstNonEmpty(rememberedModel.suffix) ||
            email !== pickFirstNonEmpty(rememberedModel.email, rememberedModel.emailAddress) ||
            phone !== pickFirstNonEmpty(rememberedModel.phoneNumber) ||
            businessName !== pickFirstNonEmpty(rememberedModel.businessCompanyName, rememberedModel.businessAddress?.companyName) ||
            businessTitle !== pickFirstNonEmpty(rememberedModel.businessPositionName, rememberedModel.businessAddress?.title);

        if (personalInfoChanged) {
            return true;
        }

        const homeAddressChanged = shouldIncludeHomeAddress && !addressesMatch({
            streetAddress: street,
            city,
            state,
            zipCode
        }, rememberedModel.homeAddress);

        if (homeAddressChanged) {
            return true;
        }

        const businessAddressChanged = shouldIncludeBusinessAddress && !addressesMatch({
            streetAddress: businessStreet,
            city: businessCity,
            state: businessState,
            zipCode: businessZipCode
        }, rememberedModel.businessAddress);

        return businessAddressChanged;
    }

    // Build remembered user object from userContainer if available.
    // Production userModel uses GivenNames/Surname/EmailAddress, while local mocks may use firstName/lastName/email.
    $: rememberedUser = isRemembered && userContainer?.userModel ? {
        firstName: pickFirstNonEmpty(userContainer.userModel.firstName, userContainer.userModel.givenNames),
        lastName: pickFirstNonEmpty(userContainer.userModel.lastName, userContainer.userModel.surname),
        email: pickFirstNonEmpty(userContainer.userModel.email, userContainer.userModel.emailAddress),
        honorific: pickFirstNonEmpty(userContainer.userModel.honorific),
        suffix: pickFirstNonEmpty(userContainer.userModel.suffix),
        phone: pickFirstNonEmpty(userContainer.userModel.phoneNumber),
        address: normalizeAddress(userContainer.userModel.homeAddress),
        businessAddress: userContainer.userModel.businessAddress ? {
            name: pickFirstNonEmpty(userContainer.userModel.businessCompanyName, userContainer.userModel.businessAddress.companyName),
            title: pickFirstNonEmpty(userContainer.userModel.businessPositionName, userContainer.userModel.businessAddress.title),
            ...normalizeAddress(userContainer.userModel.businessAddress)
        } : undefined
    } : null;

    let showRememberBanner = rememberedUser !== null;
    let useRememberedInfo = false;
    let isReturningUserEditing = false; // True when remembered user clicks "Edit info"

    // Form data - Personal Info
    let honorific = '';
    let firstName = '';
    let lastName = '';
    let suffix = '';
    let email = '';
    let phone = '';
    let fax = '';
    let mobileNumber = '';
    
    // Form data - Home Address
    let street = '';
    let city = '';
    let state = '';
    let zipCode = '';
    
    // Form data - Business
    let businessName = '';
    let businessTitle = '';
    let businessStreet = '';
    let businessCity = '';
    let businessState = '';
    let businessZipCode = '';
    
    // Form data - Options
    let rememberMe = rememberMeDefault;
    // Initialize subscription from remembered user's membership or use defaults from settings
    let subscribe = isRemembered && userContainer?.membership 
        ? userContainer.membership.receivesMessages 
        : formSettings.subscribeCheckedDefault;
    let textSubscribe = isRemembered && userContainer?.membership 
        ? userContainer.membership.receivesTextMessages 
        : formSettings.textSubscribeCheckedDefault;
    
    // Custom field values
    let customFieldValues: Record<number, string> = {};
    
    // Validated addresses (with checksums) from ActionForm
    let validatedHomeAddress: Address | null = null;
    let validatedBusinessAddress: Address | null = null;

    function updateRememberedSnapshot(user: (User & Record<string, any>) | null) {
        if (!user) {
            isRemembered = false;
            rootSettings.isRemembered = false;
            userContainer.userModel = null;
            userContainer.membership = null;
            return;
        }

        isRemembered = true;
        rootSettings.isRemembered = true;
        userContainer.userModel = {
            ...userContainer.userModel,
            ...user,
            firstName: firstName || user.givenNames || userContainer.userModel?.firstName || '',
            lastName: lastName || user.surname || userContainer.userModel?.lastName || '',
            givenNames: firstName || user.givenNames || userContainer.userModel?.givenNames || '',
            surname: lastName || user.surname || userContainer.userModel?.surname || '',
            email: email || user.emailAddress || userContainer.userModel?.email || '',
            emailAddress: email || user.emailAddress || userContainer.userModel?.emailAddress || '',
            honorific: honorific || user.honorific || userContainer.userModel?.honorific || '',
            suffix: suffix || user.suffix || userContainer.userModel?.suffix || '',
            phoneNumber: phone || user.phoneNumber || userContainer.userModel?.phoneNumber || '',
            homeAddress: user.homeAddress ?? userContainer.userModel?.homeAddress ?? null,
            businessAddress: user.businessAddress ?? userContainer.userModel?.businessAddress ?? null,
            businessCompanyName: businessName || user.businessCompanyName || userContainer.userModel?.businessCompanyName || '',
            businessPositionName: businessTitle || user.businessPositionName || userContainer.userModel?.businessPositionName || ''
        };
        userContainer.membership = {
            receivesMessages: subscribe,
            receivesTextMessages: textSubscribe
        };
    }

    async function refreshRememberedState() {
        try {
            const remembered = await getUser();
            if (!remembered?.userToken) {
                updateRememberedSnapshot(null);
                return null;
            }

            const rememberedRecord = remembered as User & Record<string, any>;
            const rememberedHasHomeAddress = Object.prototype.hasOwnProperty.call(rememberedRecord, 'homeAddress');
            const rememberedHasBusinessAddress = Object.prototype.hasOwnProperty.call(rememberedRecord, 'businessAddress');

            let membership = null;
            try {
                membership = await getMemberships({ userToken: remembered.userToken });
            } catch (error) {
                console.warn('Failed to load remembered membership:', error);
            }

            isRemembered = true;
            rootSettings.isRemembered = true;
            userContainer.userModel = {
                ...userContainer.userModel,
                ...remembered,
                firstName: pickFirstNonEmpty(rememberedRecord.firstName, remembered.givenNames, userContainer.userModel?.firstName),
                lastName: pickFirstNonEmpty(rememberedRecord.lastName, remembered.surname, userContainer.userModel?.lastName),
                givenNames: pickFirstNonEmpty(remembered.givenNames, rememberedRecord.firstName, userContainer.userModel?.givenNames),
                surname: pickFirstNonEmpty(remembered.surname, rememberedRecord.lastName, userContainer.userModel?.surname),
                email: pickFirstNonEmpty(rememberedRecord.email, remembered.emailAddress, userContainer.userModel?.email),
                emailAddress: pickFirstNonEmpty(remembered.emailAddress, rememberedRecord.email, userContainer.userModel?.emailAddress),
                honorific: pickFirstNonEmpty(remembered.honorific, userContainer.userModel?.honorific),
                suffix: pickFirstNonEmpty(rememberedRecord.suffix, userContainer.userModel?.suffix),
                phoneNumber: pickFirstNonEmpty(remembered.phoneNumber, userContainer.userModel?.phoneNumber),
                homeAddress: rememberedHasHomeAddress ? (remembered.homeAddress ?? null) : (userContainer.userModel?.homeAddress ?? null),
                businessAddress: rememberedHasBusinessAddress ? (remembered.businessAddress ?? null) : (userContainer.userModel?.businessAddress ?? null),
                businessCompanyName: pickFirstNonEmpty(rememberedRecord.businessCompanyName, userContainer.userModel?.businessCompanyName),
                businessPositionName: pickFirstNonEmpty(rememberedRecord.businessPositionName, userContainer.userModel?.businessPositionName)
            };

            if (membership) {
                userContainer.membership = {
                    receivesMessages: membership.isSubscriber,
                    receivesTextMessages: membership.isTextSubscriber ?? false
                };
            }

            return remembered;
        } catch (error) {
            console.warn('Failed to refresh remembered user:', error);
            updateRememberedSnapshot(null);
            return null;
        }
    }

    async function saveUserWithCurrentIdentity(userPayload: User): Promise<User | null> {
        if (!userPayload.userToken) {
            return await postUser(userPayload);
        }

        try {
            return await putUser(userPayload);
        } catch (error) {
            const isUnauthorizedUpdate = error instanceof ApiResponseError
                && (error.statusCode === 403 || (error.statusCode === 500 && /unauthorized/i.test(error.message)));

            if (!isUnauthorizedUpdate) {
                throw error;
            }

            await refreshRememberedState();
            const refreshedToken = userContainer?.userModel?.userToken;
            const refreshedUserId = userContainer?.userModel?.userId;
            if (!refreshedToken || refreshedToken === userPayload.userToken) {
                throw error;
            }

            userPayload.userToken = refreshedToken;
            userPayload.userId = refreshedUserId || userPayload.userId;
            return await putUser(userPayload);
        }
    }

    function pruneOptionalBlankFields(userPayload: User & Record<string, any>) {
        const optionalStringFields = [
            'honorific',
            'suffix',
            'phoneNumber',
            'mobileNumber',
            'businessCompanyName',
            'businessPositionName'
        ];

        optionalStringFields.forEach((field) => {
            if (typeof userPayload[field] === 'string' && userPayload[field].trim() === '') {
                delete userPayload[field];
            }
        });

        if (!userPayload.homeAddress?.checksum) {
            delete userPayload.homeAddress;
        }

        if (!userPayload.businessAddress?.checksum) {
            delete userPayload.businessAddress;
        }

        if (!userPayload.customFieldValues || userPayload.customFieldValues.length === 0) {
            delete userPayload.customFieldValues;
        }
    }

    function extractIdentityChoices(data: unknown): UserIdentity[] {
        const parsedData = typeof data === 'string'
            ? (() => {
                try {
                    return JSON.parse(data);
                } catch {
                    return [];
                }
            })()
            : data;

        if (!Array.isArray(parsedData)) return [];

        return parsedData.filter((identity): identity is UserIdentity =>
            identity
            && typeof identity.id === 'number'
            && typeof identity.givenNames === 'string'
            && typeof identity.surname === 'string'
            && typeof identity.token === 'string'
        );
    }

    function openIdentityChooser(choices: UserIdentity[]) {
        identityChoices = choices;
        selectedIdentityToken = choices[0]?.token || '';
        isChoosingIdentity = choices.length > 1;

        if (choices.length === 1) {
            verifiedIdentity = choices[0];
            void handleSubmit();
        }
    }

    function handleVerifiedUser(identity: UserIdentity) {
        verifiedIdentity = identity;
        isAwaitingVerification = false;
        void handleSubmit();
    }

    function handleVerificationFailure() {
        errorMessage = 'We could not verify your email address. Please try again.';
    }

    function handleVerificationNotFound() {
        errorMessage = 'We could not find a matching user for that verification code.';
    }

    function handleIdentityChoiceConfirm() {
        const selectedIdentity = identityChoices.find((identity) => identity.token === selectedIdentityToken);
        if (!selectedIdentity) return;

        verifiedIdentity = selectedIdentity;
        isChoosingIdentity = false;
        void handleSubmit();
    }

    function handleIdentityChoiceCancel() {
        isChoosingIdentity = false;
        identityChoices = [];
        selectedIdentityToken = '';
        verifiedIdentity = null;
    }

    function populateFormFromRememberedUser() {
        if (!rememberedUser) return;

        const rememberedHomeAddress = userContainer?.userModel?.homeAddress;
        const rememberedBusinessAddress = userContainer?.userModel?.businessAddress;
        const shouldUseHomeAddress = formSettings.homeSectionVisible;
        const shouldUseBusinessAddress = formSettings.businessSectionVisible && formSettings.businessAddressVisible;
        
        // Personal info
        honorific = rememberedUser.honorific || '';
        firstName = rememberedUser.firstName || '';
        lastName = rememberedUser.lastName || '';
        suffix = rememberedUser.suffix || '';
        email = rememberedUser.email || '';
        phone = rememberedUser.phone || '';
        
        // Home address
        if (shouldUseHomeAddress && rememberedUser.address) {
            street = rememberedUser.address.street || '';
            city = rememberedUser.address.city || '';
            state = rememberedUser.address.state || '';
            zipCode = rememberedUser.address.zipCode || '';
        } else if (!shouldUseHomeAddress) {
            street = '';
            city = '';
            state = '';
            zipCode = '';
        }
        validatedHomeAddress = shouldUseHomeAddress ? getValidatedAddress(rememberedHomeAddress) : null;
        
        // Business info
        if (rememberedUser.businessAddress) {
            businessName = rememberedUser.businessAddress.name || '';
            businessTitle = rememberedUser.businessAddress.title || '';
        }

        if (shouldUseBusinessAddress && rememberedUser.businessAddress) {
            businessStreet = rememberedUser.businessAddress.street || '';
            businessCity = rememberedUser.businessAddress.city || '';
            businessState = rememberedUser.businessAddress.state || '';
            businessZipCode = rememberedUser.businessAddress.zipCode || '';
        } else if (!shouldUseBusinessAddress) {
            businessStreet = '';
            businessCity = '';
            businessState = '';
            businessZipCode = '';
        }
        validatedBusinessAddress = shouldUseBusinessAddress ? getValidatedAddress(rememberedBusinessAddress) : null;
    }

    async function completeConfirmationForSignedUser(userToken: string, userId: number, datePreviouslySigned: string | null = null) {
        if (!petition) return;

        await loadRelatedPetitions(petition.id);

        let actualSignatureCount: number | undefined = undefined;
        try {
            const refreshedPetition = await getPetitionById(petition.id);
            if (refreshedPetition?.signatureCount && refreshedPetition.signatureCount > 0) {
                actualSignatureCount = refreshedPetition.signatureCount;
            }
        } catch {
            actualSignatureCount = petition.signatureCount && petition.signatureCount > 0
                ? petition.signatureCount
                : undefined;
        }

        const shareData = await getShareData(petition.id, petition.url, userToken);
        sessionStorage.setItem(getShareTokenStorageKey(petition.id), userToken);

        setConfirmationData({
            petitionId: petition.id,
            petitionTitle: petition.title,
            signatureCount: actualSignatureCount,
            signatureGoal: petition.signatureGoal,
            shareUrl: shareData.shareUrl,
            facebookShareUrl: shareData.facebookShareUrl,
            twitterShareUrl: shareData.twitterShareUrl,
            linkedInShareUrl: shareData.linkedInShareUrl,
            thankYouMessage: petition.confirmationMessage || 'Thank you for signing this petition!',
            sharingEnabled: petition.sharingEnabled ?? true,
            relatedPetitions,
            datePreviouslySigned
        });
        navigateToConfirmation(petition.id);
    }

    async function continueAsRememberedUser() {
        if (!petition) return;

        pageState = 'submitting';
        errorMessage = '';

        try {
            await refreshRememberedState();
            const userToken = userContainer?.userModel?.userToken;
            const userId = userContainer?.userModel?.userId;

            if (!userToken || !userId) {
                populateFormFromRememberedUser();
                await handleSubmit();
                return;
            }

            await signPetition(petition.id, {
                userId,
                userToken
            });

            await completeConfirmationForSignedUser(userToken, userId);
        } catch (error) {
            if (error instanceof ApiResponseError && error.statusCode === 409) {
                const userToken = userContainer?.userModel?.userToken;
                const userId = userContainer?.userModel?.userId;

                if (userToken && userId) {
                    const signatureStatus = await getUserPetitionSignatureStatus(petition.id, userToken);
                    await completeConfirmationForSignedUser(userToken, userId, signatureStatus?.dateSigned || null);
                    return;
                }
            }

            errorMessage = error instanceof Error ? error.message : t.Failed_To_Sign_Petition();
            pageState = 'form';
        }
    }

    function handleUseRememberedInfo() {
        verifiedIdentity = null;
        useRememberedInfo = true;
        pageState = 'submitting';
        showRememberBanner = false;
        void continueAsRememberedUser();
    }

    function handleEditInfo() {
        verifiedIdentity = null;
        showRememberBanner = false;
        isReturningUserEditing = true; // Show subscriptionTextIdentified
        populateFormFromRememberedUser();
    }

    async function handleNotMe() {
        showRememberBanner = false;
        isReturningUserEditing = false; // Show regular subscriptionText
        verifiedIdentity = null;
        isAwaitingVerification = false;
        isChoosingIdentity = false;
        identityChoices = [];
        selectedIdentityToken = '';
        
        // Delete remember me cookies (like Directory page does)
        try {
            await deleteRememberMeCookies();
        } catch (error) {
            console.warn('Failed to delete remember me cookies:', error);
        }
        updateRememberedSnapshot(null);
        
        // Clear user token so a NEW user will be created on submit (not update old user)
        if (userContainer?.userModel) {
            userContainer.userModel.userToken = '';
            userContainer.userModel.userId = 0;
        }
        
        // Reset checkbox defaults
        rememberMe = rememberMeDefault;
        subscribe = formSettings.subscribeCheckedDefault;
        textSubscribe = formSettings.textSubscribeCheckedDefault;
        
        // Clear validated addresses
        validatedHomeAddress = null;
        validatedBusinessAddress = null;
        
        // Clear all form data
        honorific = '';
        firstName = '';
        lastName = '';
        suffix = '';
        email = '';
        phone = '';
        fax = '';
        mobileNumber = '';
        street = '';
        city = '';
        state = '';
        zipCode = '';
        businessName = '';
        businessTitle = '';
        businessStreet = '';
        businessCity = '';
        businessState = '';
        businessZipCode = '';
        customFieldValues = {};
    }

    async function handleSubmit() {
        if (!petition) return;
        
        pageState = 'submitting';
        errorMessage = '';
        let isUpdatingExistingUser = false;
        let failedDuringSignPetition = false;
        
        try {
            const shouldIncludeHomeAddress = formSettings.homeSectionVisible;
            const shouldIncludeBusinessAddress = formSettings.businessSectionVisible && formSettings.businessAddressVisible;
            const isEditingRememberedUser = isReturningUserEditing && !!rememberedUser && !!userContainer?.userModel?.userToken && !verifiedIdentity;
            const homeAddressChanged = !isEditingRememberedUser || !addressesMatch({
                streetAddress: street,
                city,
                state,
                zipCode
            }, userContainer?.userModel?.homeAddress);
            const businessAddressChanged = !isEditingRememberedUser || !addressesMatch({
                streetAddress: businessStreet,
                city: businessCity,
                state: businessState,
                zipCode: businessZipCode
            }, userContainer?.userModel?.businessAddress);
            const rememberedProfileChanged = !isEditingRememberedUser || hasRememberedProfileChanges(shouldIncludeHomeAddress, shouldIncludeBusinessAddress);

            // Step 1: Check if addresses are validated (ActionForm validates with debouncing)
            // Only consider address valid if it has a checksum (matching AddressSearch behavior)
            if (shouldIncludeHomeAddress && homeAddressChanged && street && zipCode && (!validatedHomeAddress || !validatedHomeAddress.checksum)) {
                // Address entered but not validated - try to validate now
                try {
                    const response = await checkAddress(street, zipCode, city, state);
                    if (response.addresses && response.addresses.length === 1 && response.addresses[0].checksum) {
                        validatedHomeAddress = response.addresses[0];
                    } else {
                        validatedHomeAddress = null;
                    }
                } catch (e) {
                    console.warn('Home address validation failed:', e);
                    validatedHomeAddress = null;
                }
                
                if ((!validatedHomeAddress || !validatedHomeAddress.checksum) && formSettings.homeAddressMandatory) {
                    throw new Error("Sorry! We can't find that home address. Please check your entry and try again.");
                }
            }
            
            if (shouldIncludeBusinessAddress && businessAddressChanged && businessStreet && businessZipCode && (!validatedBusinessAddress || !validatedBusinessAddress.checksum)) {
                // Business address entered but not validated
                try {
                    const response = await checkAddress(businessStreet, businessZipCode, businessCity, businessState);
                    if (response.addresses && response.addresses.length === 1 && response.addresses[0].checksum) {
                        validatedBusinessAddress = response.addresses[0];
                    } else {
                        validatedBusinessAddress = null;
                    }
                } catch (e) {
                    console.warn('Business address validation failed:', e);
                    validatedBusinessAddress = null;
                }
                
                if ((!validatedBusinessAddress || !validatedBusinessAddress.checksum) && formSettings.businessAddressMandatory) {
                    throw new Error("Sorry! We can't find that business address. Please check your entry and try again.");
                }
            }

            // Step 2: Build user object from form data with validated addresses
            const userPayload: User = {
                honorific: honorific || undefined,
                givenNames: firstName,
                surname: lastName,
                emailAddress: email,
                phoneNumber: phone || undefined,
                rememberMe: rememberMe,
                membershipReceivesMessages: subscribe,
                homeAddress: shouldIncludeHomeAddress && homeAddressChanged && validatedHomeAddress ? {
                    streetAddress: validatedHomeAddress.streetAddress,
                    city: validatedHomeAddress.city,
                    state: validatedHomeAddress.state,
                    zipCode: validatedHomeAddress.zipCode,
                    zipCodeExtension: validatedHomeAddress.zipCodeExtension,
                    county: validatedHomeAddress.county,
                    country: validatedHomeAddress.country || formSettings.country || 'US',
                    checksum: validatedHomeAddress.checksum
                } : undefined,
                businessAddress: shouldIncludeBusinessAddress && businessAddressChanged && validatedBusinessAddress ? {
                    streetAddress: validatedBusinessAddress.streetAddress,
                    city: validatedBusinessAddress.city,
                    state: validatedBusinessAddress.state,
                    zipCode: validatedBusinessAddress.zipCode,
                    zipCodeExtension: validatedBusinessAddress.zipCodeExtension,
                    county: validatedBusinessAddress.county,
                    country: validatedBusinessAddress.country || formSettings.country || 'US',
                    checksum: validatedBusinessAddress.checksum
                } : undefined
            };
            
            // Business company name and title are separate user fields (not part of address)
            if (formSettings.businessSectionVisible) {
                (userPayload as any).businessCompanyName = businessName || undefined;
                (userPayload as any).businessPositionName = businessTitle || undefined;
            }
            
            // Add custom field values if any
            if (customFields.length > 0 && Object.keys(customFieldValues).length > 0) {
                const customFieldPayload = buildCustomFieldPayload();
                if (customFieldPayload.length > 0) {
                    userPayload.customFieldValues = customFieldPayload;
                }
            }

            // Include existing userToken if we have remembered user data
            if (rememberedUser && userContainer?.userModel?.userToken) {
                userPayload.userId = userContainer.userModel.userId || undefined;
                userPayload.userToken = userContainer.userModel.userToken;
            }

            if (!userPayload.userToken && verifiedIdentity) {
                userPayload.userId = verifiedIdentity.id;
                userPayload.userToken = verifiedIdentity.token;
            }

            pruneOptionalBlankFields(userPayload as User & Record<string, any>);
            isUpdatingExistingUser = Boolean(userPayload.userToken);

            // Step 3: Save/create user via API
            // Use putUser if we have a token (updating existing user), otherwise postUser (new user)
            const savedUser = isEditingRememberedUser && !rememberedProfileChanged
                ? {
                    userId: userContainer.userModel.userId,
                    userToken: userContainer.userModel.userToken
                } as User
                : await saveUserWithCurrentIdentity(userPayload);

            if (!savedUser || !savedUser.userToken || !savedUser.userId) {
                throw new Error('Failed to save user information');
            }

            // Step 4: Handle membership/subscription
            // The postMemberships API uses user.membershipReceivesMessages and membershipReceivesTextMessages
            // to set the subscription flags. mobileNumber is required when receivesTextMessages is true.
            savedUser.membershipReceivesMessages = subscribe;
            savedUser.membershipReceivesTextMessages = textSubscribe;
            savedUser.mobileNumber = mobileNumber || undefined;
            await postMemberships(savedUser);

            // Step 5: Handle remember me cookies
            if (rememberMe) {
                await postRememberMeCookies(savedUser);
                updateRememberedSnapshot(savedUser);
                await refreshRememberedState();
            } else {
                // Clear remember me cookies if unchecked
                await deleteRememberMeCookies();
                updateRememberedSnapshot(null);
                await refreshRememberedState();
            }

            // Step 6: Sign the petition
            failedDuringSignPetition = true;
            const signatureRequest: SignPetitionRequest = {
                userId: savedUser.userId,
                userToken: savedUser.userToken
            };
            
            await signPetition(petition.id, signatureRequest);
            failedDuringSignPetition = false;

            // Load related petitions for the confirmation page
            await loadRelatedPetitions(petition.id);
            
            let actualSignatureCount: number | undefined = undefined;
            try {
                const refreshedPetition = await getPetitionById(petition.id);
                if (refreshedPetition?.signatureCount && refreshedPetition.signatureCount > 0) {
                    actualSignatureCount = refreshedPetition.signatureCount;
                }
            } catch {
                actualSignatureCount = undefined;
            }
            
            const shareData = await getShareData(petition.id, petition.url, savedUser.userToken);
            sessionStorage.setItem(getShareTokenStorageKey(petition.id), savedUser.userToken);

            // Set confirmation data in store and navigate
            setConfirmationData({
                petitionId: petition.id,
                petitionTitle: petition.title,
                signatureCount: actualSignatureCount,
                signatureGoal: petition.signatureGoal,
                shareUrl: shareData.shareUrl,
                facebookShareUrl: shareData.facebookShareUrl,
                twitterShareUrl: shareData.twitterShareUrl,
                linkedInShareUrl: shareData.linkedInShareUrl,
                thankYouMessage: petition.confirmationMessage || 'Thank you for signing this petition!',
                sharingEnabled: petition.sharingEnabled ?? true,
                relatedPetitions
            });
            navigateToConfirmation(petition.id);
        } catch (error) {
            // Check for specific error codes
            if (error instanceof ApiResponseError) {
                if (!failedDuringSignPetition && error.statusCode === 300) {
                    const choices = extractIdentityChoices(error.data);
                    if (choices.length > 0) {
                        pageState = 'form';
                        openIdentityChooser(choices);
                        return;
                    }
                }

                if (!failedDuringSignPetition && error.statusCode === 409 && !isUpdatingExistingUser) {
                    verificationEmailAddress = email;
                    isAwaitingVerification = true;
                    pageState = 'form';
                    return;
                }

                if (failedDuringSignPetition && error.statusCode === 409) {
                    // User already signed this petition - show confirmation with date
                    await loadRelatedPetitions(petition.id);
                    
                    const alreadySignedCount = petition.signatureCount && petition.signatureCount > 0
                        ? petition.signatureCount
                        : undefined;

                    // Get the user's signature status to find when they signed
                    const userToken = userContainer?.userModel?.userToken;
                    let datePreviouslySigned: string | null = null;
                    if (userToken) {
                        const signatureStatus = await getUserPetitionSignatureStatus(petition.id, userToken);
                        datePreviouslySigned = signatureStatus?.dateSigned || null;
                        sessionStorage.setItem(getShareTokenStorageKey(petition.id), userToken);
                    }
                    
                    const shareData = await getShareData(petition.id, petition.url, userToken);

                    setConfirmationData({
                        petitionId: petition.id,
                        petitionTitle: petition.title,
                        signatureCount: alreadySignedCount,
                        signatureGoal: petition.signatureGoal,
                        shareUrl: shareData.shareUrl,
                        facebookShareUrl: shareData.facebookShareUrl,
                        twitterShareUrl: shareData.twitterShareUrl,
                        linkedInShareUrl: shareData.linkedInShareUrl,
                        thankYouMessage: petition.confirmationMessage || 'Thank you for signing this petition!',
                        sharingEnabled: petition.sharingEnabled ?? true,
                        relatedPetitions,
                        datePreviouslySigned
                    });
                    navigateToConfirmation(petition.id);
                    return;
                }
            }
            
            errorMessage = error instanceof Error ? error.message : t.Failed_To_Sign_Petition();
            pageState = 'form';
        }
    }

    function handleGoBack() {
        window.location.href = `/${associationIdName}${getSiteSegment()}/Petitions`;
    }

    function handleGoHome() {
        window.location.href = `/${associationIdName}/home`;
    }
</script>

<div class={`${isEmbeddedPluginContext ? '' : 'min-h-screen'} bg-white`} data-testid="sign-petition-page">
    {#if pageState === 'loading'}
        <!-- Loading State - Skeleton -->
        <SignPageSkeleton />
    {:else if pageState === 'error'}
        <!-- Error State -->
        <div class={`flex items-center justify-center ${isEmbeddedPluginContext ? '' : 'min-h-screen'}`} data-testid="sign-page-error">
            <div class="text-center px-4">
                <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                    </svg>
                </div>
                <h2 class="!text-xl !leading-7 !font-semibold !text-gray-900 mb-2" data-testid="sign-page-error-title">{t.Unable_To_Load_Petition()}</h2>
                <p class="!text-base !leading-6 !text-gray-600 mb-6" data-testid="sign-page-error-message">{errorMessage}</p>
                {#if !hideBackToPetitions}
                    <Button variant="primary" class="!min-h-[48px] !text-base !leading-6" on:click={handleGoBack} data-testid="sign-page-back-button">
                        {t.Back_To_Petitions_Button()}
                    </Button>
                {:else}
                    <Button variant="primary" class="!min-h-[48px] !text-base !leading-6" on:click={handleGoHome} data-testid="sign-page-home-button">
                        {t.Home_Label()}
                    </Button>
                {/if}
            </div>
        </div>
    {:else if petition}
        <!-- Main Sign Page -->
        <div class="max-w-6xl mx-auto" data-testid="sign-page-content">
            <!-- Back Button (Mobile) -->
            {#if !hideBackToPetitions}
                <div class="px-4 py-3 md:hidden">
                    <button 
                        class="sign-back-nav inline-flex items-center gap-2 rounded-md px-1.5 py-1 text-gray-600 transition-colors hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                        on:click={handleGoBack}
                        data-testid="back-button-mobile"
                    >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                        </svg>
                        <span class="text-base font-medium underline-offset-2 hover:underline">{t.Back_To_Petitions_Button()}</span>
                    </button>
                </div>
            {/if}

            <!-- Split Layout: Desktop -->
            <div class={`relative flex flex-col lg:flex-row ${isEmbeddedPluginContext ? '' : 'lg:min-h-screen'}`}>
                {#if !petition.hideAlert}
                    <div class="hidden lg:block absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gray-200 pointer-events-none"></div>
                    <!-- Left Side: Hero + Petition Text -->
                    <div class={`lg:w-1/2 ${isEmbeddedPluginContext ? '' : 'lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto'}`}>
                        <div class="p-4 lg:p-8">
                            <!-- Back Button (Desktop) -->
                            {#if !hideBackToPetitions}
                                <div class="hidden md:block mb-6">
                                    <button 
                                        class="sign-back-nav inline-flex items-center gap-2 rounded-md px-1.5 py-1 text-gray-600 transition-colors hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                                        on:click={handleGoBack}
                                        data-testid="back-button-desktop"
                                    >
                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                                        </svg>
                                        <span class="text-base font-medium underline-offset-2 hover:underline">{t.Back_To_Petitions_Button()}</span>
                                    </button>
                                </div>
                            {/if}
                            
                            <!-- Hero -->
                            <div data-testid="petition-hero">
                                <ActionHero
                                    title={petition.title}
                                    imageUrl={petition.imageUrl}
                                    dueDate={petition.dueDate}
                                    signatureCount={hasSignatureGoal ? 0 : petition.signatureCount}
                                    spotlighted={petition.spotlighted}
                                />
                            </div>
    
                            <!-- Description (CKEditor HTML content with embed support) -->
                            {#if petition.description}
                                <div class="mt-6" data-testid="petition-description">
                                    <div class="relative">
                                        <div
                                            bind:this={descriptionElement}
                                            class="ck-content max-w-none text-base text-gray-700 leading-relaxed overflow-hidden transition-all duration-300"
                                            style="max-height: {descriptionExpanded ? 'none' : alertCollapsedHeight + 'px'};"
                                            use:initCkEditorEmbeds
                                        >
                                            {@html petition.description}
                                        </div>
                                        {#if descriptionNeedsExpansion && !descriptionExpanded}
                                            <div class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                                        {/if}
                                    </div>
                                    {#if descriptionNeedsExpansion}
                                        <div class="pt-2">
                                            <button
                                                class="text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1 transition-colors"
                                                on:click={() => descriptionExpanded = !descriptionExpanded}
                                            >
                                                {#if descriptionExpanded}
                                                    <span>{t.Show_Less()}</span>
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
                                                    </svg>
                                                {:else}
                                                    <span>{t.Read_Full_Text()}</span>
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                                                    </svg>
                                                {/if}
                                            </button>
                                        </div>
                                    {/if}
                                </div>
                            {/if}
                            
                            <!-- Petition Text (Expandable) -->
                            <div class="mt-6" data-testid="petition-text-container">
                                <PetitionText text={petition.petitionText} header={formSettings.petitionTextSectionHeading} maxHeight={180} />
                            </div>
                        </div>
                    </div>
                {/if}

                <!-- Right Side: Form -->
                <div class="{ !petition.hideAlert ? 'lg:w-1/2' : '' } bg-white" data-testid="sign-form-container">
                    <div class="{ !petition.hideAlert ? 'p-4 lg:p-8' : '' }">
                        <div class="w-full mr-auto">
                            {#if hasSignatureGoal}
                                <div class="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm" data-testid="signature-goal-card">
                                    <p class="!text-xs !leading-4 !font-semibold uppercase tracking-wide text-gray-600 mb-2">Signature goal</p>
                                    <p class="!text-base !leading-6 !font-semibold text-gray-900 mb-3">
                                        {(petition?.signatureCount ?? 0).toLocaleString()} of {signatureGoalValue.toLocaleString()} signatures
                                    </p>
                                    <div class="relative h-2.5 bg-gray-200 rounded-full overflow-hidden mb-2">
                                        <div
                                            class="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
                                            style="background-color: var(--vv-action-center-button-color, #0d9488); width: {signatureProgressPercent}%"
                                        ></div>
                                    </div>
                                    <p class="!text-sm !leading-5 !text-gray-600">
                                        {#if signaturesRemaining > 0}
                                            <span class="font-semibold" style="color: var(--vv-action-center-button-color, #0d9488);">{getRemainingSignaturesText(signaturesRemaining)}</span>
                                        {:else if (petition?.signatureCount ?? 0) > signatureGoalValue}
                                            <span class="font-semibold" style="color: var(--vv-action-center-button-color, #0d9488);">{getExceededSignaturesText((petition?.signatureCount ?? 0) - signatureGoalValue)}</span>
                                        {:else}
                                            <span class="font-semibold" style="color: var(--vv-action-center-button-color, #0d9488);">Goal reached</span>
                                        {/if}
                                    </p>
                                </div>
                            {/if}

                            <!-- Section Header -->
                            <div class="mb-6" data-testid="form-header">
                                <h2 class="sign-form-title text-lg font-semibold text-gray-900" data-testid="form-title">{t.Your_Information_Header()}</h2>
                            </div>

                            <!-- API Error Message Banner -->
                            {#if errorMessage}
                                <div class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="api-error-banner">
                                    <div class="flex items-start gap-3">
                                        <svg class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                        <div>
                                            <p class="text-sm font-medium text-red-800" data-testid="api-error-title">{t.Unable_To_Submit()}</p>
                                            <p class="text-sm text-red-700 mt-1" data-testid="api-error-message">{errorMessage}</p>
                                        </div>
                                        <button 
                                            class="ml-auto text-red-500 hover:text-red-700"
                                            on:click={() => errorMessage = ''}
                                            aria-label="Dismiss error"
                                            data-testid="api-error-dismiss"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            {/if}

                            <!-- Remember Me Banner -->
                            {#if showRememberBanner && rememberedUser}
                                <div data-testid="remember-me-banner-container">
                                    <RememberMeBanner
                                        user={rememberedUser}
                                        onUseInfo={handleUseRememberedInfo}
                                        onEdit={handleEditInfo}
                                        onNotMe={handleNotMe}
                                    />
                                </div>
                            {:else if pageState === 'submitting' && useRememberedInfo && !isReturningUserEditing}
                                <div
                                    class="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white px-6 py-12 text-center"
                                    data-testid="remembered-submit-loading"
                                >
                                    <div class="mb-4 h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-teal-600"></div>
                                    <p class="!text-base !leading-6 !text-gray-600">Submitting your signature...</p>
                                </div>
                            {:else}
                                <!-- Form (configured by Action Settings Center) -->
                                <div data-testid="action-form-container">
                                    <ActionForm
                                    settings={formSettings}
                                    {customFields}
                                    isReturningUser={isReturningUserEditing}
                                    userToken={verifiedIdentity?.token || userContainer?.userModel?.userToken}
                                    associationPhoneNumber={userContainer?.associationPhoneNumber || ''}
                                    bind:textSubscribe
                                    bind:honorific
                                    bind:firstName
                                    bind:lastName
                                    bind:suffix
                                    bind:email
                                    bind:phone
                                    bind:fax
                                    bind:mobileNumber
                                    bind:street
                                    bind:city
                                    bind:state
                                    bind:zipCode
                                    bind:businessName
                                    bind:businessTitle
                                    bind:businessStreet
                                    bind:businessCity
                                    bind:businessState
                                    bind:businessZipCode
                                    bind:rememberMe
                                    bind:subscribe
                                    bind:customFieldValues
                                    bind:validatedHomeAddress
                                    bind:validatedBusinessAddress
                                    submitLabel={t.Sign_Petition_Button()}
                                    isSubmitting={pageState === 'submitting'}
                                    onSubmit={handleSubmit}
                                />
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    {/if}
</div>

<EmailVerification
    bind:isAwaitingVerification={isAwaitingVerification}
    emailAddress={verificationEmailAddress}
    onUserVerified={handleVerifiedUser}
    onNotFound={handleVerificationNotFound}
    onFailure={handleVerificationFailure}
/>

<Modal
    bind:isOpen={isChoosingIdentity}
    headerText={t.Select_Existing_Profile_Header()}
    confirmButtonText={t.Continue_Button()}
    onConfirm={handleIdentityChoiceConfirm}
    onCancel={handleIdentityChoiceCancel}
>
    <p class="text-base text-gray-700 mb-4">
        {t.Multiple_Users_With_Email_Message()}
    </p>
    <div class="space-y-3">
        {#each identityChoices as identity}
            <label class="flex items-start gap-3 text-base text-gray-700 cursor-pointer">
                <input
                    type="radio"
                    name="identity-choice"
                    class="w-4 h-4 mt-1 radio-brand"
                    bind:group={selectedIdentityToken}
                    value={identity.token}
                />
                <span>{identity.givenNames} {identity.surname}</span>
            </label>
        {/each}
    </div>
</Modal>

<style>
    :global(.sign-form-title) {
        font-size: 1.125rem !important;
        line-height: 1.75rem !important;
        font-weight: 600 !important;
        color: #111827 !important;
        margin: 0 !important;
    }
</style>
